import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiMoreVertical } from 'react-icons/fi';
import './DataTable.css';

const DataTable = ({ 
  columns, 
  data, 
  loading, 
  error,
  onRetry,
  onRowClick,
  rowClassName,
  emptyMessage = 'No data available',
  compact = false,
  sortable = true
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const handleSort = (columnField) => {
    if (!sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === columnField && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnField, direction });
  };

  const getSortedData = () => {
    if (!sortConfig.key || !data) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle strings (case-insensitive)
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (columnField) => {
    if (!sortable || sortConfig.key !== columnField) {
      // Unsorted - show vertical dots or custom icon
      return <FiMoreVertical className="sort-icon" size={12} />;
    }
    return sortConfig.direction === 'asc' 
      ? <FiChevronUp className="sort-icon active" size={12} />
      : <FiChevronDown className="sort-icon active" size={12} />;
  };
  
  if (loading) {
    return (
      <div className="table-container">
        <div className="table-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-container">
        <div className="table-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          {onRetry && (
            <button onClick={onRetry} className="btn-secondary">
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="table-container">
        <div className="table-empty">
          <span className="empty-icon">📋</span>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  const sortedData = getSortedData();

  return (
    <div className="table-container">
      <div className="table-scroll">
      { /*<table className={compact ? 'compact' : ''}> */}
        <table className="data-table">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th 
                key={index}
                className={`${column.className || ''} ${column.sortable !== false && sortable && column.field ? 'sortable' : ''}`}
                style={{ width: column.width, minWidth: column.minWidth }}
                onClick={() => column.sortable !== false && column.field && handleSort(column.field)}
              >
                <div className="th-content">
                  <span>{column.header}</span>
                  {column.sortable !== false && column.field && sortable && getSortIcon(column.field)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIndex) => (
            <tr 
              key={row.id || rowIndex}
              onClick={() => onRowClick && onRowClick(row)}
              className={[onRowClick ? 'clickable' : '', rowClassName ? rowClassName(row) : ''].filter(Boolean).join(' ')}
            >
              {columns.map((column, colIndex) => (
                <td 
                  key={colIndex}
                  className={column.className || ''}
                >
                  {column.render ? column.render(row) : row[column.field]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default DataTable;